import axios from 'axios';

// Use axios directly for this API since it's under /api/activity_progress (not /api/children)
const getCSRFToken = () => {
  const token = document.querySelector('meta[name="csrf-token"]');
  return token ? token.getAttribute('content') : '';
};

const progressClient = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-CSRF-Token': getCSRFToken()
  }
});

// Add request interceptor to ensure CSRF token is always fresh
progressClient.interceptors.request.use(config => {
  config.headers['X-CSRF-Token'] = getCSRFToken();
  return config;
});

// Add response interceptor for 401 error handling (redirect to login)
progressClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Redirect to login if unauthorized
      // Return a never-resolving promise to prevent error handling in React
      // while the redirect is happening
      window.location.href = '/login';
      return new Promise(() => {}); // Never resolves, redirect will happen
    }
    return Promise.reject(error);
  }
);

/**
 * Create async query to fetch external service progress
 * @param {number} curriculumActivityId - ID of the curriculum activity
 * @param {number} activitySessionId - ID of the activity session (optional)
 */
export const createProgressQuery = async (curriculumActivityId, activitySessionId = null) => {
  const params = { curriculum_activity_id: curriculumActivityId };
  if (activitySessionId) {
    params.activity_session_id = activitySessionId;
  }

  const response = await progressClient.post('/activity_progress', params);
  return response.data;
};

/**
 * Poll async query status
 * @param {string} queryId - ID of the query to poll
 */
export const pollProgressQuery = async (queryId) => {
  try {
    const response = await progressClient.get(`/activity_progress/${queryId}`);
    return response.data;
  } catch (error) {
    // 202 Accepted means still processing
    if (error.response?.status === 202) {
      return error.response.data;
    }
    throw error;
  }
};

/**
 * Poll until query is complete (max 120 attempts)
 * @param {string} queryId - ID of the query to poll
 * @param {number} maxAttempts - Maximum number of attempts (default: 120)
 * @param {number} intervalMs - Polling interval in milliseconds (default: 1000)
 */
export const pollUntilComplete = async (queryId, maxAttempts = 120, intervalMs = 1000) => {
  let attempts = 0;

  while (attempts < maxAttempts) {
    const result = await pollProgressQuery(queryId);

    if (result.status === 'successful') {
      return result.outputs;
    }

    if (result.status === 'error') {
      throw new Error(result.error_message || 'Query failed');
    }

    // Wait before next attempt
    await new Promise(resolve => setTimeout(resolve, intervalMs));
    attempts++;
  }

  throw new Error('Polling timeout exceeded');
};
